import { computed, onBeforeMount, onUpdated, ref } from 'vue'
import { type RouteLocation, useRoute, useRouter } from 'vue-router'
import { useAppStateStore } from '@/features/application'
import { typeContextCourantToContextAppType } from '@/features/auth/context-app/types/ContextApp.ts'
import type { RouteName } from '@/router/route-name.ts'

export function useNavigationTabs(persistTabState: boolean) {
  const router = useRouter()
  const route = useRoute()
  const appState = useAppStateStore()

  const getRouteByName = (name: RouteName) => router.getRoutes().find((r) => r.name === name)

  // TODO : A revoir, pas la bonne méthode
  const isAccessible = (name: RouteName) =>
    !getRouteByName(name)?.meta?.requiresAdmin || appState.estAdmin

  const parent = computed(() => route.matched.at(-2))

  const tabs = computed(
    () =>
      parent.value?.children
        ?.filter((routeRecord) => isAccessible(routeRecord.name as RouteName))
        ?.map((routeRecord) => ({
          tabKey: (routeRecord.meta?.tabLabel as string) ?? (routeRecord.name as string),
          routeName: routeRecord.name as RouteName,
        })) ?? [],
  )

  const getTabsTitle = (name: RouteName) =>
    tabs.value.find((t) => t.routeName === name)?.tabKey as string

  const getTabsRoute = (tabKey: string) =>
    tabs.value.find((t) => t.tabKey === tabKey)?.routeName

  const goToTab = (tabKey: string) => {
    const routeName = getTabsRoute(tabKey)
    if (!routeName) return

    const routeLocation = persistTabState ? tabsState.value[routeName] : undefined
    if (routeLocation) {
      void router.push(routeLocation)
    } else {
      void router.push({
        name: routeName,
        params: { context: typeContextCourantToContextAppType(appState.contextCourant) },
      })
    }
  }

  const selectedTab = computed({
    get: () => getTabsTitle(route.name as RouteName) ?? tabs.value[0]?.tabKey ?? '',
    set: (value: string) => goToTab(value),
  })

  /* Gére les tabs persistants */
  const tabsState = ref<Partial<Record<RouteName, RouteLocation>>>({})
  const storeTabsState = () => {
    const routeName = route.name as RouteName
    tabsState.value[routeName] = router.currentRoute.value
  }

  onBeforeMount(storeTabsState)
  onUpdated(storeTabsState)

  return { tabs, goToTab, getTabsTitle, selectedTab }
}
